import { Dispatch, GetState } from "common/types";

import {
  pushProviderToGlobalDataLayer,
  pushProviderUserIdToGlobalDataLayer
} from "services/firebase/auth/analytics/events";
import errorTranslator, {
  ErrorMessage,
  shouldLogError
} from "services/firebase/auth/errorTranslator";
import { ProviderSSO } from "services/firebase/auth/types";

import {
  getUserAccountType,
  signInError,
  signInStart,
  signInSuccess,
  signUpSuccess
} from "seneca-common/features/user/state";
import logError from "seneca-common/utils/sentry/logError";

import { getHelpLinkRegionCode, getRegion } from "features/regions/selectors";

import authenticateWithSSO from "./authenticateWithSSO";
import completeLogin from "./completeLogin";
import completeSignup from "./completeSignup";

type Props = {
  onSignupSuccess: () => void;
  onLoginSuccess: (accountType?: string | null) => void;
  onError: (error: ErrorMessage) => void;
};

type AuthProps = {
  providerName: ProviderSSO;
  signInWithRedirect: boolean;
  accountType?: string;
};

function handleSSOAuth({
  onSignupSuccess,
  onLoginSuccess,
  onError,
  providerName,
  signInWithRedirect,
  accountType
}: Props & AuthProps) {
  return async (dispatch: Dispatch, getState: GetState) => {
    pushProviderToGlobalDataLayer(providerName);
    dispatch(signInStart());
    const region = getRegion(getState());

    try {
      const { userCredential, userProfile, isNewUser } =
        await authenticateWithSSO({ providerName, signInWithRedirect });

      pushProviderUserIdToGlobalDataLayer(
        userCredential.user.providerData?.find(
          user => user.providerId === userCredential.providerId
        )?.uid
      );

      if (isNewUser) {
        await dispatch(
          completeSignup({
            userCredential,
            userProfile,
            accountType,
            regionBeforeAuthenticating: region
          })
        );
        dispatch(signUpSuccess());
        onSignupSuccess();
      } else {
        await dispatch(completeLogin({ regionBeforeAuthenticating: region }));
        dispatch(signInSuccess(userCredential.user.uid));
        const accountTypeOnLogin = getUserAccountType(getState());
        onLoginSuccess(accountTypeOnLogin);
      }
    } catch (error: any) {
      dispatch(signInError());
      if (shouldLogError(error)) {
        logError(error, { message: "Unexpected error authenticating via SSO" });
      }
      const locale = getHelpLinkRegionCode(getState());
      onError(errorTranslator(error, locale));
    }
  };
}

export default function makeHandleSSOAuth(props: Props) {
  return (authProps: AuthProps) => handleSSOAuth({ ...props, ...authProps });
}
